// useWebSocket.ts
import { ref, onUnmounted } from 'vue'

type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

interface UseWebSocketOptions<T = any> {
  url: string
  onMessage?: (data: T) => void
  onError?: (error: Event) => void
  onOpen?: (event: Event) => void
  onClose?: (event: CloseEvent) => void
  autoReconnect?: boolean
  reconnectInterval?: number
}

export function useWebSocket<T = string>(options: UseWebSocketOptions<T>) {
  const status = ref<WebSocketStatus>('disconnected')
  const data = ref<T[]>([])
  let socket: WebSocket | null = null
  let reconnectTimer: number | null = null

  const connect = () => {
    status.value = 'connecting'

    try {
      socket = new WebSocket(options.url)

      socket.onopen = (event) => {
        status.value = 'connected'
        options.onOpen?.(event)
        if (reconnectTimer) {
          clearTimeout(reconnectTimer)
          reconnectTimer = null
        }
      }

      socket.onmessage = (event) => {
        try {
          const parsedData =
            typeof event.data === 'string'
              ? (JSON.parse(event.data) as T)
              : event.data

          data.value.push(parsedData)
          options.onMessage?.(parsedData)
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      socket.onerror = (error) => {
        status.value = 'error'
        options.onError?.(error)
        attemptReconnect()
      }

      socket.onclose = (event) => {
        status.value = 'disconnected'
        options.onClose?.(event)
        attemptReconnect()
      }
    } catch (error) {
      status.value = 'error'
      console.error('WebSocket initialization error:', error)
      attemptReconnect()
    }
  }

  const send = (message: T) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const dataToSend =
        typeof message === 'string' ? message : JSON.stringify(message)
      socket.send(dataToSend)
    }
  }

  const close = () => {
    if (socket) {
      socket.close()
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket = null
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  const attemptReconnect = () => {
    if (options.autoReconnect && !reconnectTimer) {
      reconnectTimer = window.setTimeout(() => {
        connect()
      }, options.reconnectInterval || 3000)
    }
  }

  onUnmounted(() => {
    close()
  })

  connect()

  return {
    status,
    data,
    connect,
    send,
    close,
  }
}
